// src/admin/AdminImageManager.js
import { useEffect, useState } from "react";
import { storage, auth } from "../firebase";
import {
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import "./AdminDashboard.css";

export default function AdminImageManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [deleting, setDeleting] = useState(false);

  const folderPath = "aboutus-images";

  const loadImages = async () => {
    setLoading(true);
    setError("");
    setSelected({});

    try {
      const folderRef = ref(storage, folderPath);
      const result = await listAll(folderRef);

      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url,
          };
        })
      );

      files.sort((a, b) => (a.name < b.name ? 1 : -1));
      setImages(files);
    } catch (err) {
      console.error("LOAD ERROR:", err);
      setError(err.message || "Failed to load images.");
    }

    setLoading(false);
  };

  useEffect(() => {
    loadImages();
  }, []);

  const toggleSelect = (fullPath) => {
    setSelected((prev) => ({
      ...prev,
      [fullPath]: !prev[fullPath],
    }));
  };

  const selectAll = () => {
    if (Object.keys(selected).length === images.length) {
      setSelected({});
    } else {
      const all = {};
      images.forEach((img) => (all[img.fullPath] = true));
      setSelected(all);
    }
  };

  const deleteSelected = async () => {
    if (!auth.currentUser) {
      alert("You must be logged in to delete images.");
      return;
    }

    const selectedImgs = images.filter((img) => selected[img.fullPath]);

    if (selectedImgs.length === 0) {
      alert("No images selected.");
      return;
    }

    const ok = window.confirm(
      `Delete ${selectedImgs.length} image(s)? This cannot be undone.`
    );
    if (!ok) return;

    setDeleting(true);
    setError("");

    let successCount = 0;
    let failCount = 0;

    for (const img of selectedImgs) {
      try {
        const fileRef = ref(storage, img.fullPath);
        await deleteObject(fileRef);
        successCount++;
      } catch (err) {
        console.error("DELETE ERROR:", err);
        failCount++;
      }
    }

    // Refresh list
    await loadImages();
    setDeleting(false);

    alert(
      `Deleted: ${successCount}\n` +
      (failCount ? `Failed: ${failCount}` : "")
    );
  };

  return (

    
    <div className="admin-page">

    {/* Go Back Button Above Title */}
    <div style={{ marginBottom: "15px" }}>
      <button
        className="admin-button small"
        onClick={() => (window.location.href = "/admin/homepage")}
      >
        ← Go Back
      </button>
    </div>

    <h1>About Us Images</h1>

      <div className="admin-image-manager-header">
        <button
          className="admin-button small"
          onClick={loadImages}
          disabled={loading || deleting}
        >
          {loading ? "Refreshing…" : "Refresh List"}
        </button>

        <button
          className="admin-button danger small"
          onClick={deleteSelected}
          disabled={deleting}
        >
          {deleting ? "Deleting…" : "Delete Selected"}
        </button>

        <button
          className="admin-button small"
          onClick={selectAll}
        >
          {Object.keys(selected).length === images.length
            ? "Unselect All"
            : "Select All"}
        </button>

        <span className="admin-image-count">
          {images.length} image{images.length === 1 ? "" : "s"}
        </span>
      </div>

      {error && <div className="admin-error-box">{error}</div>}

      {loading && <div className="admin-loading">Loading images…</div>}

      <div className="admin-image-grid">
        
        {images.map((img) => (
          <div key={img.fullPath} className="admin-image-card">
            <input
              type="checkbox"
              className="admin-image-checkbox"
              checked={!!selected[img.fullPath]}
              onChange={() => toggleSelect(img.fullPath)}
            />

            <div className="admin-image-thumb-wrap">
              <img src={img.url} alt={img.name} className="admin-image-thumb" />
            </div>

            <div className="admin-image-meta">
              <div className="admin-image-name">{img.name}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
